import React, { useEffect } from "react";
import {
    Box,
    Divider,
    Grid,
    TextInput,
    Text,
    Group,
    Button,
    Select,
    Accordion,
    Tooltip
} from "@mantine/core";
import type { IFormularioClienteVistaProps } from "../interfaces";
import { IconAddressBook, IconAt, IconCancel, IconDeviceFloppy, IconFilterCancel, IconPasswordUser, IconPencilCheck, IconPhone, IconPlus, IconReplaceUser, IconSearch, IconSocial, IconTrash, IconUser, IconUserScan } from "@tabler/icons-react";
import type { IContactoCliente } from "../../../modelos/Clientes/entidades/IContactoCliente";

const FormularioClienteVista: React.FunctionComponent<IFormularioClienteVistaProps> = ({
    clienteSeleccionado,
    manejarCambioValorCliente,
    agregarEditarCliente,
    tiposIdentificacion,
    agregarError,
    errores,
    renovarCliente,
    enRenovacionCliente,
    proyecto,
    consultarClientePorIdentificacion,
    clienteConsultadoIdentificacion,
    limpiarCampos,
    agregarErrorContacto,
    erroresContactos,
    consultarClienteActivo
}) => {


    useEffect(() => {
        if (proyecto.ClienteId !== null) {
            consultarClienteActivo(proyecto.ClienteId || 0);
        }
    }, []);

    const agregarContacto = () => {
        const nuevoContacto: IContactoCliente = { ClienteId: clienteSeleccionado.Id, Email: '', EsPrincipal: false, Id: 0, Nombre: '', Telefono: '' };
        manejarCambioValorCliente(
            "ContactoClientes",
            [...clienteSeleccionado.ContactoClientes, nuevoContacto]
        );
    };

    const eliminarContacto = (index: number) => {
        if (clienteSeleccionado.ContactoClientes.length <= 1 || index === 0) return;
        const nuevosContactos = clienteSeleccionado.ContactoClientes.filter((_, i) => i !== index);
        manejarCambioValorCliente("ContactoClientes", nuevosContactos);
    };

    const proyectoConCliente = proyecto.ClienteId !== null;
    const sinClienteSeleccionado = clienteSeleccionado.Id === 0;
    const clienteDiferente = clienteSeleccionado.Id !== 0 && proyecto.ClienteId !== clienteSeleccionado.Id;

    const mostrarCancelarRenovacion =
        (proyectoConCliente && sinClienteSeleccionado && enRenovacionCliente) ||
        (proyectoConCliente && clienteDiferente && enRenovacionCliente);

    return (
        <Box>
            {(clienteSeleccionado.Id !== 0 && proyecto.ClienteId !== null) && clienteSeleccionado.Id === proyecto.ClienteId ? (
                <Group justify="flex-start" pb="sm">
                    <Button
                        onClick={() => renovarCliente(true)}
                        color="primary"
                        variant="light"
                        size='compact-sm'
                        rightSection={<IconReplaceUser opacity={100} size={15} />}
                        radius="lg"
                    >
                        <Text fw={500} fz={11}>Renovar Cliente</Text>
                    </Button>
                </Group>
            ) :
                mostrarCancelarRenovacion && (
                    <Group justify="flex-start" pb="sm">
                        <Button
                            onClick={() => renovarCliente(false)}
                            color="primary"
                            variant="light"
                            size='compact-sm'
                            rightSection={<IconCancel opacity={100} size={15} />}
                            radius="lg"
                        >
                            <Text fw={500} fz={11}>Cancelar Renovacion</Text>
                        </Button>
                    </Group>)}
            <Grid gutter="md">
                <Grid.Col span={{ base: 12, md: 3 }}>
                    <Select
                        label="Tipo Identificación"
                        placeholder="Tipo Identificación"
                        size="xs"
                        required
                        leftSection={<IconUserScan size={14} />}
                        disabled={clienteSeleccionado.Id !== 0}
                        comboboxProps={{ zIndex: 5000 }}
                        data={tiposIdentificacion.map(tp => ({
                            value: tp.IdTipoIdentificacion.toString(),
                            label: tp.TipoIdentificacion,
                        }))}
                        nothingFoundMessage="No se encontraron tipos de identificación"
                        value={clienteSeleccionado.TipoIdentificacionId !== 0 ? clienteSeleccionado.TipoIdentificacionId.toString() : undefined}
                        onChange={(value) => {
                            manejarCambioValorCliente("TipoIdentificacionId", value ? Number(value) : 0)
                            manejarCambioValorCliente("TipoIdentificacionNombre", value ? tiposIdentificacion.find(tp => tp.IdTipoIdentificacion === Number(value))?.TipoIdentificacion || '' : '')
                            if (value === null) {
                                agregarError('TipoIdentificacionId', 'Selecciona un tipo de identificación')
                            }
                            else {
                                agregarError('TipoIdentificacionId', '')
                            }
                        }}
                        error={errores.TipoIdentificacionId}
                    />
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 3 }}>
                    <TextInput
                        label="Identificación"
                        placeholder="Identificación"
                        name="Identificacion"
                        size="xs"
                        leftSection={<IconPasswordUser size={14} />}
                        disabled={clienteSeleccionado.Id !== 0}
                        maxLength={30}
                        rightSection={clienteSeleccionado.Identificacion !== '' && clienteSeleccionado.Id === 0 ? <IconSearch cursor='pointer' onClick={() => consultarClientePorIdentificacion(clienteSeleccionado.Identificacion)} size={14} /> : null}
                        value={clienteSeleccionado.Identificacion || ""}
                        onChange={(event) => {
                            const regex = /^[a-zA-Z0-9._-]*$/;
                            const valor = event.currentTarget.value;

                            if (regex.test(valor)) {
                                manejarCambioValorCliente("Identificacion", valor);
                            }
                            if (valor === "") {
                                agregarError("Identificacion", "Este campo es requerido");
                            } else {
                                agregarError("Identificacion", "");
                            }
                        }}
                        required
                    />

                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 3 }}>
                    <TextInput
                        label="Nombre Cliente"
                        placeholder="Nombre Cliente"
                        name="NombreCliente"
                        leftSection={<IconUser size={14} />}
                        size="xs"
                        disabled={!clienteConsultadoIdentificacion}
                        maxLength={50}
                        value={clienteSeleccionado.Nombre || ""}
                        onChange={(event) => {
                            manejarCambioValorCliente("Nombre", event.currentTarget.value)
                            if (event.target.value === '') {
                                agregarError('Nombre', 'Este campo es requerido')
                            }
                            else {
                                agregarError('Nombre', '')
                            }
                        }}
                        error={errores.Nombre}
                        required
                    />
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 3 }}>
                    <TextInput
                        label="Razón Social"
                        placeholder="Razón Social"
                        name="RazonSocial"
                        disabled={clienteSeleccionado.TipoIdentificacionId === 1 || clienteSeleccionado.TipoIdentificacionId === 0 || !clienteConsultadoIdentificacion}
                        size="xs"
                        leftSection={<IconSocial size={14} />}
                        maxLength={50}
                        value={clienteSeleccionado.RazonSocial || ""}
                        onChange={(event) => {
                            manejarCambioValorCliente("RazonSocial", event.currentTarget.value)
                            if (event.target.value === '' && clienteSeleccionado.TipoIdentificacionId === 2) {
                                agregarError('RazonSocial', 'Este campo es requerido')
                            }
                            else {
                                agregarError('RazonSocial', '')
                            }
                        }}
                        error={errores.RazonSocial}
                        required={clienteSeleccionado.TipoIdentificacionId === 2}
                    />
                </Grid.Col>
            </Grid>

            <Divider my="md" />
        
            {/* --- Datos Contacto --- */}
            <Accordion __size="xs" variant="contained" radius="md">
                <Accordion.Item value="Datos Contacto">
                    <Accordion.Control disabled={!clienteConsultadoIdentificacion}>
                        <Group justify="space-between" w="100%">
                            <Text color={Object.values(erroresContactos).some(contacto => Object.values(contacto).some(error => error && error !== '')) ? '#f95a5a' : 'black'} fw={600} fz="sm">Datos Contacto</Text>
                            <Button
                                mr={8}
                                disabled={!clienteConsultadoIdentificacion}
                                size="compact-sm"
                                leftSection={<IconPlus size={14} />}
                                variant="light"
                                radius='lg'
                                color="primary"
                                onClick={e => {
                                    e.stopPropagation();
                                    agregarContacto();
                                }}
                            >
                                <Text fw={500} fz={11}>Agregar Contacto</Text>
                            </Button>
                        </Group>
                    </Accordion.Control>
                    <Accordion.Panel bg="#fff" p={8}>
                        <Grid gutter="xl">
                            {clienteSeleccionado.ContactoClientes.map((c, posicion) => (
                                <Grid.Col key={posicion} span={{md: 6, xs: 12}}>
                                    <Grid gutter="md">
                                        <Group justify="space-between" align="center">
                                            <Text fw={600} fz={13}>
                                                {`Contacto ${posicion + 1}`}
                                            </Text>
                                            {posicion !== 0 && (
                                                <Tooltip label="Eliminar contacto" withArrow>
                                                    <Button
                                                        variant="subtle"
                                                        color="primary"
                                                        size="xs"
                                                        disabled={!clienteConsultadoIdentificacion}
                                                        px={4}
                                                        onClick={() => eliminarContacto(posicion)}
                                                        style={{ minWidth: 0, height: 28, display: 'flex', alignItems: 'center' }}
                                                    >
                                                        <IconTrash size={16} style={{ display: 'block' }} />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </Group>
                                        <Grid.Col span={{ base: 12, md: 12 }}>
                                            <TextInput
                                                label={`Nombre Contacto ${posicion + 1}`}
                                                placeholder={`Nombre Contacto ${posicion + 1}`}
                                                name={`NombreContacto${posicion + 1}`}
                                                size="xs"
                                                leftSection={<IconAddressBook size={14} />}
                                                disabled={!clienteConsultadoIdentificacion}
                                                maxLength={100}
                                                value={c.Nombre || ""}
                                                onChange={event => {
                                                    const nuevosContactos = [...clienteSeleccionado.ContactoClientes];
                                                    nuevosContactos[posicion] = {
                                                        ...nuevosContactos[posicion],
                                                        Nombre: event.currentTarget.value
                                                    };
                                                    manejarCambioValorCliente("ContactoClientes", nuevosContactos);
                                                    if (event.currentTarget.value === "" && posicion === 0) {
                                                        agregarErrorContacto(posicion, "Nombre", "Este campo es requerido");
                                                    } else {
                                                        agregarErrorContacto(posicion, "Nombre", "");
                                                    }
                                                }}
                                                error={erroresContactos[posicion]?.Nombre}
                                                required={posicion === 0}
                                            />
                                        </Grid.Col>

                                        <Grid.Col span={{ base: 12, md: 6 }}>
                                            <TextInput
                                                label={`Telefono Contacto ${posicion + 1}`}
                                                placeholder={`Telefono Contacto ${posicion + 1}`}
                                                name={`TelefonoContacto${posicion + 1}`}
                                                size="xs"
                                                leftSection={<IconPhone size={14} />}
                                                disabled={!clienteConsultadoIdentificacion}
                                                maxLength={20}
                                                value={c.Telefono || ""}
                                                onChange={event => {
                                                    const nuevosContactos = [...clienteSeleccionado.ContactoClientes];
                                                    nuevosContactos[posicion] = {
                                                        ...nuevosContactos[posicion],
                                                        Telefono: event.currentTarget.value
                                                    };
                                                    manejarCambioValorCliente("ContactoClientes", nuevosContactos);
                                                    if (event.currentTarget.value === "" && posicion === 0) {
                                                        agregarErrorContacto(posicion, "Telefono", "Este campo es requerido");
                                                    } else {
                                                        agregarErrorContacto(posicion, "Telefono", "");
                                                    }
                                                }}
                                                error={erroresContactos[posicion]?.Telefono}
                                                required={posicion === 0}
                                            />
                                        </Grid.Col>

                                        <Grid.Col span={{ base: 12, md: 6 }}>
                                            <TextInput
                                                label={`Correo Contacto ${posicion + 1}`}
                                                placeholder={`Correo Contacto ${posicion + 1}`}
                                                name={`CorreoContacto${posicion + 1}`}
                                                size="xs"
                                                leftSection={<IconAt size={14} />}
                                                disabled={!clienteConsultadoIdentificacion}
                                                maxLength={150}
                                                value={c.Email || ""}
                                                onChange={event => {
                                                    const nuevosContactos = [...clienteSeleccionado.ContactoClientes];
                                                    nuevosContactos[posicion] = {
                                                        ...nuevosContactos[posicion],
                                                        Email: event.currentTarget.value
                                                    };
                                                    manejarCambioValorCliente("ContactoClientes", nuevosContactos);
                                                    if (event.currentTarget.value === "" && posicion === 0) {
                                                        agregarErrorContacto(posicion, "Email", "Este campo es requerido");
                                                    } else {
                                                        agregarErrorContacto(posicion, "Email", "");
                                                    }
                                                }}
                                                error={erroresContactos[posicion]?.Email}
                                                required={posicion === 0}
                                            />
                                        </Grid.Col>
                                    </Grid>
                                </Grid.Col>
                            ))}
                        </Grid>
                    </Accordion.Panel>
                </Accordion.Item>
            </Accordion>

            <Box pos="sticky" bottom={0} bg="white">
                <Group justify="flex-end" pt="sm" gap="xs">
                    {clienteSeleccionado.Id !== 0 && proyecto.ClienteId === null && (
                        <Button
                            onClick={limpiarCampos}
                            color="gray"
                            variant="default"
                            size="compact-sm"
                            rightSection={<IconFilterCancel opacity={100} color="black" size={15} />}
                            radius="lg"
                        >
                            <Text fw={500} fz={11}>Limpiar</Text>
                        </Button>
                    )}

                    <Button
                        onClick={agregarEditarCliente}
                        color="primary"
                        variant="filled"
                        size="compact-sm"
                        rightSection={
                            clienteSeleccionado.Id
                                ? <IconPencilCheck opacity={100} color="white" size={15} />
                                : <IconDeviceFloppy opacity={100} color="white" size={15} />
                        }
                        radius="lg"
                    >
                        <Text fw={500} fz={11}>
                            {clienteSeleccionado.Id === proyecto.ClienteId
                                ? "Editar Cliente"
                                : clienteSeleccionado.Id !== 0 && clienteSeleccionado.Id !== proyecto.ClienteId
                                    ? "Editar y Relacionar cliente"
                                    : "Guardar y Relacionar Cliente"}
                        </Text>
                    </Button>
                </Group>
            </Box>

        </Box>
    );
};

export default FormularioClienteVista;
